import React from 'react'
import { graphql } from 'gatsby'
import {
  BuildingOffice2Icon,
  MagnifyingGlassCircleIcon,
} from '@heroicons/react/24/outline'
import { Button, Info } from '../../ui'
import HeaderMedia from '../../ui/headerMedia'
import Logo from '../../ui/logo'
import { Firm } from './mapper'
import FirmSocials from './socials/socials'
import ButtonColor from '../../ui/buttonColor'
import { useApplyStore } from '../../../stores/ui/applyStore'

type FirmHeaderProps = {
  firm: Firm
  careerSite: boolean
  customButton?: React.ReactNode
}

const FirmHeader: React.FC<FirmHeaderProps> = ({
  firm,
  careerSite,
  customButton = undefined,
}) => {
  const height = careerSite ? 'lg:top-[268px]' : 'lg:top-[348px]'
  const { openApplicationFormModal } = useApplyStore()

  return (
    <div className="container mb-8 lg:mb-16">
      <div className="relative">
        <HeaderMedia
          headerMediaImage={firm.header}
          colorCareerSite={firm?.colorCareerSite || undefined}
        />
      </div>
      {careerSite && firm.website && (
        <div className="absolute text-white top-5 left-10">
          <a
            href={firm.website}
            rel="noopener noreferrer"
            target="_blank"
            title={`Site web de ${firm.name}`}
          >
            {`< Site web`}
          </a>
        </div>
      )}
      <div
        className={`absolute top-32 right-0 left-0 text-center text-white w-screen ${height} xl:w-[1115px] lg:w-[950px] lg:mx-auto 2xl:w-[1320px]`}
      >
        <div className="flex flex-col items-center lg:flex-row lg:items-start">
          <div className="space-y-4">
            <div
              className="shadow w-40 h-40 bg-white rounded-xl p-2
                lg:top-[25px] lg:w-[150px] lg:h-[150px]"
            >
              <Logo
                image={firm?.logo ? firm?.logo : firm?.logoUrl}
                alt={firm.name}
                additionalClasses="h-full w-full object-contain"
              />
            </div>
            {firm.socials && (
              <FirmSocials
                socials={firm.socials}
                header
                color={careerSite ? firm.colorCareerSite : null}
                additionalClasses="hidden lg:block"
              />
            )}
          </div>
          <div className="flex flex-col items-center h-40 mt-32 justify-evenly lg:items-start lg:mt-0 lg:justify-start">
            <div className="gap-4 lg:flex lg:flex-col lg:items-start lg:ml-5">
              <h1 className="font-semibold text-[25px] text-center lg:text-left lg:mx-0">
                {firm?.name}
              </h1>
              <div className="flex flex-wrap items-center justify-center gap-x-4">
                {firm.totalEmployees && (
                  <div className="">
                    <Info
                      icon="lama"
                      color="white"
                      value={firm.totalEmployees}
                      size="sm"
                    />
                  </div>
                )}
                <div className="inline-flex items-center gap-x-2">
                  <BuildingOffice2Icon className="w-5 h-5 text-white" />
                  <span className="text-white">{`${firm?.nbOffices} ${
                    firm?.nbOffices === 1 ? 'bureau' : 'bureaux'
                  }`}</span>
                </div>
                {firm.website && (
                  <a
                    href={firm.website}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span
                      className="inline-flex items-center mt-1 gap-x-2"
                      aria-label={`Accéder au site web de ${firm.name}`}
                    >
                      <MagnifyingGlassCircleIcon className="w-5 h-5 text-white" />
                      <span className="text-white">Site web</span>
                    </span>
                  </a>
                )}
              </div>
              {firm.socials && (
                <div className="mt-8">
                  <FirmSocials
                    socials={firm.socials}
                    header
                    color={careerSite ? firm.colorCareerSite : null}
                    additionalClasses="lg:hidden flex flex-col items-center gap-4"
                  />
                </div>
              )}
              <div className="right-0 mt-28 lg:mt-36 lg:absolute lg:-top-36">
                {customButton ||
                  (careerSite ? (
                    <ButtonColor
                      label="Candidature spontanée"
                      invert
                      color={firm.colorCareerSite || 'primary'}
                      onClick={() =>
                        openApplicationFormModal({
                          offices: firm.offices || undefined,
                          colorCareerSite: firm.colorCareerSite || undefined,
                          isCareerSiteApply: true,
                        })
                      }
                    />
                  ) : (
                    <Button
                      label="Candidature spontanée"
                      invert
                      color="primary"
                      onClick={() =>
                        openApplicationFormModal({
                          offices: firm.offices || undefined,
                        })
                      }
                    />
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FirmHeader

export const query = graphql`
  fragment FirmHeader on STRAPI_ACCOUNTING_FIRM {
    name
    website
    totalEmployees
    header {
      url
      localFile {
        childImageSharp {
          gatsbyImageData
        }
      }
    }
    logo {
      url
      localFile {
        childImageSharp {
          gatsbyImageData
        }
        extension
        url
      }
    }
    offices {
      id
    }
  }
`
